import { useState } from 'react';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';

import { FormRow } from '@platform-ui/form';

import { InputOtp as BasicInputOtp } from '../../ui/input-otp';
import type { InputOtpProps as BasicInputOtpProps } from '../../ui/input-otp';

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>
  extends
    Omit<BasicInputOtpProps, 'name' | 'onChange' | 'onBlur' | 'value'>,
    UseControllerReturn<TFieldValues, TName> {
  confirmationError?: React.ReactNode;
}

export function InputOtp<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>) {
  const {
    field: { ref, ...field },
    fieldState,
    formState: _formState,
    required,
    confirmed,
    confirmationError,
    ...inputProps
  } = props;

  const [isFocused, setFocused] = useState(false);

  const hasError =
    (!isFocused && typeof fieldState.error !== 'undefined') ||
    !!confirmationError;

  const errorMessage = fieldState.error?.message || confirmationError;

  const isValid = !hasError && confirmed;

  return (
    <FormRow
      withoutOffset
      required={required}
      error={hasError}
      errorMessage={errorMessage}
    >
      <BasicInputOtp
        {...field}
        {...inputProps}
        refCallback={ref}
        onClean={() => field.onChange('')}
        valid={isValid}
        required={required}
        error={hasError}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          field.onBlur();
        }}
      />
    </FormRow>
  );
}
